import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, getAuthToken, formatApiError, clearLegacyMockAdminState } from "@/lib/api";

const AuthContext = createContext(null);

function normalizeAdminUser(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const email = typeof data.email === "string" ? data.email.trim() : "";
  const role = typeof data.role === "string" ? data.role.trim().toLowerCase() : "";
  if (!email || role !== "admin") return false;
  return { ...data, email, role };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const nextUser = normalizeAdminUser(data);
      if (!nextUser) {
        setAuthToken(null);
        setUser(false);
        return;
      }
      setUser(nextUser);
    } catch (err) {
      setAuthToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearLegacyMockAdminState();
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      const nextUser = normalizeAdminUser(data?.user);
      if (!nextUser || !data?.access_token) {
        setAuthToken(null);
        setUser(false);
        return { ok: false, error: "Admin login failed. Please verify your credentials and backend configuration." };
      }
      setAuthToken(data.access_token);
      setUser(nextUser);
      return { ok: true };
    } catch (err) {
      setAuthToken(null);
      setUser(false);
      return { ok: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore — proceed with client cleanup
    }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
